import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { RxDbProvider } from '@/providers/rxdb/rx-hooks';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';

import { LocalDataInitCancelledError, LocalDataLeftByLogoutError, localDataService } from './local-data-service';
import { searchService } from '@/services/search-service';

import { useServices } from '@/providers/services-provider';
import { getAuthUser } from '@flintmint/web-shared/hooks/use-auth-user';
import { capture } from '@/analytics';

import { appRoutes } from '@/constants/routes';
import type { LogoutRouteState } from '@/features/logout';
import { appReadyAtom, initialSyncCompleteAtom, initialSyncResolvedAtom, knownEmptyAccountAtom } from '@/state';
import type { RxDocumintDatabase } from '@flintmint/core-shared/rxdb';

import styles from './local-data-provider.module.scss';

const LOCAL_DATA_PROVIDER_CANCELLED = Symbol('LOCAL_DATA_PROVIDER_CANCELLED');

const LocalDataProvider = ({ children }: PropsWithChildren) => {
  const { id: userId } = getAuthUser();
  const { firestore } = useServices();
  const { isAuthenticated, isLoading } = useAuth0();
  const navigate = useNavigate();
  const [readyForReplication, setReadyForReplication] = useState(false);
  const [localDataReady, setLocalDataReady] = useState(false);
  const [rxDB, setRxDB] = useState<RxDocumintDatabase>();
  const [leftByLogout, setLeftByLogout] = useState(false);
  const setAppReady = useSetAtom(appReadyAtom);
  const setInitialSyncComplete = useSetAtom(initialSyncCompleteAtom);
  const setInitialSyncResolved = useSetAtom(initialSyncResolvedAtom);
  const setKnownEmptyAccount = useSetAtom(knownEmptyAccountAtom);
  const isColdStartRef = useRef(false);
  const appReadyCapturedRef = useRef(false);
  const forcedLogoutRef = useRef(false);
  const mountedAtMsRef = useRef(performance.now());

  // The logout route explains the unopenable database and asks before discarding unsynced changes.
  const forceLogout = useCallback(() => {
    navigate(appRoutes.logout, { state: { localDataError: true } satisfies LogoutRouteState });
  }, [navigate]);

  // A detached rebuild that rejects with a recoverable RxDB error (DB6/COL12) must reach the same
  // self-repair the init catch uses — logout deletes the wedged local DB. Idempotent across both
  // rebuild catches (they may share one in-flight rebuild promise) and gated on lifecycle so a
  // logout/unmount teardown rejection stays quiet. Returns true when it owns the recovery so the
  // caller skips its non-recoverable log path.
  const recoverFromRebuildErrorIfNeeded = useCallback(
    (error: unknown, isActive: boolean): boolean => {
      if (!isActive || forcedLogoutRef.current) return false;
      if (!isRecoverableLocalDataInitError(error)) return false;
      forcedLogoutRef.current = true;
      forceLogout();
      return true;
    },
    [forceLogout]
  );

  const captureAppReadyOnce = useCallback(async () => {
    if (appReadyCapturedRef.current) return;
    appReadyCapturedRef.current = true;

    const timeToUsableMs = Math.round(performance.now() - mountedAtMsRef.current);
    // Hand the usable delta to the service so it can copy time_to_usable_ms onto its
    // sync events (self-contained startup records) without importing this provider.
    localDataService.recordUsableAt(timeToUsableMs);

    try {
      capture('app_ready', {
        time_to_usable_ms: timeToUsableMs,
        is_cold_start: isColdStartRef.current,
        doc_count: await localDataService.countUserDocs(),
      });
    } catch {
      // Telemetry must never throw or affect app startup.
    }
  }, []);

  useEffect(() => {
    let isActive = true;

    // Before anything opens local data: the active search worker spawns at module eval, which
    // on a fresh sign-in happens before the user is known. Put it on this user's index slot
    // now, so no data can flow through a worker holding another account's key (or none).
    searchService.ensureStarted();

    // Private-area entry baseline: all startup timings measure from provider mount, so the
    // service can compute time_to_synced_ms without importing this provider.
    localDataService.recordAppEntry(mountedAtMsRef.current);

    const awaitIfActive = async <T,>(promise: Promise<T>): Promise<T> => {
      const result = await promise;
      if (!isActive) {
        throw LOCAL_DATA_PROVIDER_CANCELLED;
      }
      return result;
    };

    // Prime the search index from local data, if any. First-time users (no local data) get
    // their index built by the replication path instead. Not awaited by app readiness: the
    // answer comes from the worker's own read of the saved index, which can wait behind a slow
    // database, and the index status keeps search consumers in their loading state meanwhile.
    const primeSearchIndexFromLocalData = async (hasLocalData: boolean): Promise<void> => {
      const hasPersistedIndex = await awaitIfActive(searchService.hasPersistedIndex());
      // Claimed only now, when acting on the answer, so a mount cancelled while waiting for it
      // leaves the decision to the next mount.
      if (!searchService.claimStartupIndexDecision()) {
        return;
      }
      if (hasPersistedIndex) {
        // Fire-and-forget: still opens the worker's persist gate (needed so
        // post-load edit batches persist), but app readiness must not wait on the restore
        // and a rejection must never block it. On logout terminate() rejects this promise;
        // the isActive gate keeps that expected teardown quiet.
        searchService.replicationCompleted({ persistIndex: false }).catch((error) => {
          if (isActive) {
            console.error('[LOCAL_DATA] Failed to mark search index replication completed:', error);
          }
        });
        // Fire-and-forget: the restored index may still hold docs archived within the
        // search-hook buffer window right before the previous close/reload; sweep them
        // out without blocking app readiness.
        localDataService.evictArchivedDocsFromSearchIndex().catch((error) => {
          if (isActive) {
            console.error('[LOCAL_DATA] Failed to evict archived docs from search index:', error);
          }
        });
        return;
      }

      if (!hasLocalData) {
        return;
      }

      // RxDB data exists but index is missing, likely index schema changed - rebuild the index
      // (fire-and-forget: the index status stays 'rebuilding' until done, which keeps search
      // consumers in their loading state, so app readiness need not wait).
      localDataService.rebuildSearchIndex().catch((error) => {
        if (recoverFromRebuildErrorIfNeeded(error, isActive)) return;
        if (isActive) {
          // Error object only — no document data or user content (PHI).
          Sentry.captureException(error);
          console.error('[LOCAL_DATA] Failed to rebuild search index:', error);
        }
      });
    };

    const initLocalData = async () => {
      try {
        const rxDB = await awaitIfActive(localDataService.initRxDB());
        setRxDB(rxDB);
        setReadyForReplication(true);

        // The absence of usable local data is the cold-start signal reported by app_ready.
        const hasLocalData = await awaitIfActive(localDataService.hasLocalData());
        isColdStartRef.current = !hasLocalData;
        setLocalDataReady(true);

        // Its own steps handle their failures; only an unmount (cancellation) ends it early.
        primeSearchIndexFromLocalData(hasLocalData).catch((error) => {
          if (error !== LOCAL_DATA_PROVIDER_CANCELLED) throw error;
        });
      } catch (error) {
        if (error === LOCAL_DATA_PROVIDER_CANCELLED || error instanceof LocalDataInitCancelledError) return;

        if (isRecoverableLocalDataInitError(error)) {
          forceLogout();
          return;
        }

        if (error instanceof LocalDataLeftByLogoutError) {
          setLeftByLogout(true);
          return;
        }

        console.error('[LOCAL_DATA] Failed to initialize local data:', error);
      }
    };

    void initLocalData();

    return () => {
      isActive = false;
    };
  }, [forceLogout, recoverFromRebuildErrorIfNeeded]);

  // appReady = local data init done (the search index is primed in the background). Firestore is
  // deliberately not part of this condition, so the app becomes usable offline.
  useEffect(() => {
    if (localDataReady) {
      setAppReady(true);
      captureAppReadyOnce();
    }
  }, [localDataReady, setAppReady, captureAppReadyOnce]);

  // Mirror the initial-sync latch (persisted, multi-tab RxState) into the atom. Follower
  // tabs never resolve awaitInitialReplication, so they can only learn the value from the
  // leader's RxState write via this subscription. The sync state exists once initRxDB
  // resolves, so gating on rxDB is sufficient. Mark resolved on the first emission so
  // consumers can distinguish "don't know yet" from "genuinely incomplete".
  useEffect(() => {
    if (!rxDB) return;

    const subscription = localDataService.initialSyncComplete$().subscribe((complete) => {
      setInitialSyncComplete(complete);
      setInitialSyncResolved(true);
    });

    return () => subscription.unsubscribe();
  }, [rxDB, setInitialSyncComplete, setInitialSyncResolved]);

  // Mirror the service's empty-account signal (probe-driven, see local-data-service.ts)
  // into the atom. The underlying subject exists for the service's whole lifetime, so no
  // rxDB gate is needed; the service resets it to false on clearRxDB.
  useEffect(() => {
    const subscription = localDataService.knownEmptyAccount$().subscribe(setKnownEmptyAccount);

    return () => subscription.unsubscribe();
  }, [setKnownEmptyAccount]);

  // Self-heal a corrupt persisted index: when the worker reports a failed restore, rebuild the
  // index from local RxDB data (non-blocking; rebuildSearchIndex drives the status back to ready).
  // Gated on rxDB so rxCollections exist before rebuilding; subscription tears down on unmount so a
  // logout race cannot fire against a torn-down database (terminate() also resets the status).
  useEffect(() => {
    if (!rxDB) return;

    let isActive = true;
    const subscription = searchService.indexStatus.subscribe((status) => {
      if (status !== 'restore-failed') return;

      localDataService.rebuildSearchIndex().catch((error) => {
        if (recoverFromRebuildErrorIfNeeded(error, isActive)) return;
        if (isActive) {
          // Error object only — no document data or user content (PHI).
          Sentry.captureException(error);
          console.error('[LOCAL_DATA] Failed to rebuild search index after failed restore:', error);
        }
      });
    });

    return () => {
      isActive = false;
      subscription.unsubscribe();
    };
  }, [rxDB, recoverFromRebuildErrorIfNeeded]);

  useEffect(() => {
    if (!readyForReplication || !firestore || !userId || (!isAuthenticated && isLoading)) {
      return;
    }

    let isActive = true;
    const initReplication = async () => {
      try {
        await localDataService.initReplication(firestore, userId);
      } catch (error) {
        if (!isActive) return;

        console.error('[LOCAL_DATA] Failed to initialize replication:', error);
      }
    };

    void initReplication();

    return () => {
      isActive = false;
    };
  }, [readyForReplication, firestore, isAuthenticated, isLoading, userId]);

  // Archived docs replicate through a separate pull-only partition outside the initial-sync
  // latch. Start it once the app is usable (the appReady condition) — always, not lazily on
  // an archive view: docs archived on another device must not stay stale-active locally.
  useEffect(() => {
    if (!localDataReady || !firestore || !userId || (!isAuthenticated && isLoading)) {
      return;
    }

    let isActive = true;
    const initArchivedReplication = async () => {
      try {
        await localDataService.initArchivedReplication(firestore, userId);
      } catch (error) {
        if (!isActive) return;

        console.error('[LOCAL_DATA] Failed to initialize archived replication:', error);
      }
    };

    void initArchivedReplication();

    return () => {
      isActive = false;
    };
  }, [localDataReady, firestore, isAuthenticated, isLoading, userId]);

  if (leftByLogout) {
    return <LeftByLogout />;
  }

  return <RxDbProvider db={rxDB}>{children}</RxDbProvider>;
};

// A previous logout's local data is still held open by another tab, so it could not be deleted and
// this tab will not open it (see LocalDataLeftByLogoutError).
const LeftByLogout = () => (
  <div className={styles.leftByLogout}>
    <h1>Close other DocuMint tabs</h1>
    <p>Another open DocuMint tab is still holding data from a previous session.</p>
    <button onClick={() => window.location.reload()}>Try again</button>
  </div>
);

const isRecoverableLocalDataInitError = (error: unknown): boolean => {
  return typeof error === 'object' && error !== null && 'code' in error && (error.code === 'DB6' || error.code === 'COL12');
};

export { LocalDataProvider };
